/**
 * Coaching guide reads/writes (issues #219/#220, epic #315).
 *
 * The API deliberately serves guide content from TWO different endpoints
 * with two different shapes, and this module's job is to hide that seam
 * from every component that renders a guide:
 *
 *   - `GET /learn/categories/:category/:slug` (learnGuides.ts) — the
 *     player-facing route. Only ever returns a PUBLISHED guide; 404s for
 *     everyone, admin included, otherwise (see that file's own tests —
 *     "admin can read everything" is exactly the habit the gate exists to
 *     refuse). Shape: a flat `PublishedGuide`, snake_case `review.reviewer_name`
 *     / `review.reviewed_at`, top-level `source`.
 *   - `GET /admin/guides/:category/:slug` (adminGuides.ts) — admin-only,
 *     returns the RAW content regardless of publish state, `{ guide, review }`
 *     with `review: null` when nobody has signed off yet. `guide` carries
 *     `provenance.source` instead of a flat `source`, and `review` (when
 *     present) is the repo-layer `GuideReview` shape — camelCase
 *     `reviewerName` / `reviewedAt`.
 *
 * `normalizeGuide()` below is the one place that reconciles them into a
 * single shape every renderer (`GuidePage.jsx`) and list (`AdminGuides.jsx`)
 * can use without caring which endpoint answered.
 */
import api from "./api";

/** @typedef {{
 *   slug: string, title: string, category: string, guide_type: string,
 *   surface: string, order: number, key_theme: string,
 *   featured_professional: {name: string|null, paragraph: string} | null,
 *   common_mistakes: {symptom: string, consequence: string|null}[],
 *   technique_breakdown: string[] | null,
 *   drill: {
 *     purpose: string, setup: string, equipment: string[], reps: string,
 *     coaching_points: string[], progressions: string[],
 *     common_errors: {failure: string, why: string|null, correction: string|null}[],
 *   } | null,
 *   sections: {type: string, heading: string, blocks: object[]}[],
 *   safety_notes: string[] | undefined,
 *   exercises: {number: number, name: string, content: string[]}[] | undefined,
 *   at_a_glance: {difficulty: string, duration: string} | undefined,
 *   field_diagram: {
 *     caption: string, boundary_m: number, disclaimer: string,
 *     positions: {id: string, name: string, angle: number, depth_m: number,
 *                 distance_category: string, source: string}[],
 *   } | undefined,
 *   provenanceSource: string,
 *   published: boolean,
 *   reviewerName: string | null,
 *   reviewedAt: string | null,
 * }} NormalizedGuide
 */

/** @returns {NormalizedGuide} */
export function normalizePublished(guide) {
  const { review, source, ...rest } = guide;
  return {
    ...rest,
    provenanceSource: source,
    published: true,
    reviewerName: review.reviewer_name,
    reviewedAt: review.reviewed_at,
  };
}

/** @returns {NormalizedGuide} */
export function normalizeAdmin(guide, review) {
  const { provenance, ...rest } = guide;
  return {
    ...rest,
    provenanceSource: provenance.source,
    published: Boolean(review),
    reviewerName: review?.reviewerName ?? null,
    reviewedAt: review?.reviewedAt ?? null,
  };
}

/**
 * Fetches one guide for the guide page (`/learn/:category/:slug`).
 *
 * Tries the public, player-facing route first — the same URL a player
 * eventually uses, per spec §2.2.1 — and only falls back to the admin
 * route (full content regardless of publish state) when that 404s AND the
 * caller is plausibly an admin. A non-admin who hits an unpublished slug
 * gets exactly the public route's 404, nothing more: the fallback attempt
 * is gated on `isAdmin` client-side too so a non-admin's console never even
 * issues the second request that would 403 anyway.
 *
 * @returns {Promise<{ guide: NormalizedGuide, mode: "published" | "review" }>}
 */
export async function fetchGuideForPage(category, slug, isAdmin) {
  try {
    const { data } = await api.get(`/learn/categories/${category}/${slug}`);
    return { guide: normalizePublished(data.guide), mode: "published" };
  } catch (err) {
    if (err.response?.status !== 404 || !isAdmin) throw err;
    const { data } = await api.get(`/admin/guides/${category}/${slug}`);
    return { guide: normalizeAdmin(data.guide, data.review), mode: "review" };
  }
}

/** The console's list (issue #219 AC1/AC5) — every guide, its state. */
export async function fetchAdminGuideList() {
  const { data } = await api.get("/admin/guides");
  return data.guides;
}

/** "Mark as reviewed" — publishes by recording the sign-off. */
export async function publishGuide(category, slug) {
  const { data } = await api.post(`/admin/guides/${category}/${slug}/review`);
  return data.review;
}

/** Issue #219's "unpublish" — withdraws the sign-off. */
export async function unpublishGuide(category, slug) {
  const { data } = await api.delete(`/admin/guides/${category}/${slug}/review`);
  return data.removed;
}

/** The published list a player/coach sees, optionally narrowed to one
 * destination surface (`batting`/`bowling`/`fielding`/`fitness`/`mindset`/
 * `nutrition` — #503 re-mapped mindset off the never-built `reflections`
 * surface, #514 moved nutrition off fitness onto its own tab). */
export async function fetchPublishedGuides(surface) {
  const { data } = await api.get("/learn/guides", { params: surface ? { surface } : undefined });
  return data.guides;
}

/**
 * Guide ASSIGNMENT (issue #382) — "my coach asked me to read this".
 *
 * Every one of these hits `/learn/assignments`, the same router as the
 * gated reads above, and every one of them is enforced server-side: the
 * API refuses to assign a guide that is not published, and refuses to
 * assign to a player the caller does not hold an ACTIVE coach link with.
 * Nothing in this module is the gate — hiding a button is a courtesy, not
 * a control, and `api/test/guideAssignments.test.ts` is where the rule is
 * actually proven.
 */

/** What this player has been asked to read. Omit `playerId` for "my own"
 * (the API defaults a player caller to their own profile); a coach must
 * pass one they are linked to. */
export async function fetchAssignedGuides(playerId) {
  const { data } = await api.get("/learn/assignments", {
    params: playerId ? { player_id: playerId } : undefined,
  });
  return data.assignments;
}

/** The coach's act. Rejects (404) for an unpublished guide or a player this
 * coach cannot see — the caller shows one message for both, because the
 * server deliberately does not distinguish them. */
export async function assignGuide(playerId, category, slug) {
  const { data } = await api.post("/learn/assignments", { player_id: playerId, category, slug });
  return data;
}

/** The coach's undo. */
export async function unassignGuide(assignmentId) {
  await api.delete(`/learn/assignments/${assignmentId}`);
}
